/**
 * Products, and the size x colour grid.
 *
 * A product is a style. A variant is one size in one colour - that is what has
 * stock, a barcode and, if you want, its own price. Creating a product generates
 * the whole grid at once, and every square of it gets a barcode immediately.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { compareSizes } from '@pos/shared';
import type { CatalogItem } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useToast } from '../../core/state/toast.js';
import { useAuth } from '../../core/state/auth.js';
import { api, type ApiError } from '../../core/api.js';
import { LogoError, photoFromFile } from '../../core/image.js';
import { useSync } from '../../core/sync.js';
import { db } from '../../core/db.js';
import { Button, Confirm, Empty, Field, Loading, Modal, MoneyInput, Pill, Swatch } from '../../core/ui/ui.js';
import { Barcode, Photo } from '../../core/ui/bits.js';
import { LabelPrinter } from './Labels.js';

interface Ref {
  id: string;
  name_en: string;
  name_ar: string;
  hex?: string;
  sizes?: string[];
  kind?: string;
}

export interface Refs {
  categories: Ref[];
  brands: Ref[];
  suppliers: { id: string; name: string }[];
  colors: Ref[];
  sizeScales: Ref[];
}

const NO_REFS: Refs = { categories: [], brands: [], suppliers: [], colors: [], sizeScales: [] };

/** The reference lists every product screen needs. */
export function useRefs() {
  const [refs, setRefs] = useState<Refs>(NO_REFS);

  /** Exposed so a screen that ADDS to these lists can show the result at once. */
  const reload = useCallback(async () => {
    try {
      const [categories, brands, suppliers, colors, sizeScales] = await Promise.all([
        api<Ref[]>('/catalog/categories'),
        api<Ref[]>('/catalog/brands'),
        api<{ id: string; name: string }[]>('/catalog/suppliers'),
        api<Ref[]>('/catalog/colors'),
        api<Ref[]>('/catalog/size-scales'),
      ]);
      setRefs({ categories, brands, suppliers, colors, sizeScales });
    } catch {
      /* offline: the editor needs a connection anyway */
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return useMemo(() => ({ ...refs, reload }), [refs, reload]);
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export function Products() {
  const t = useT();
  const { lang } = useI18n();
  const { can } = useAuth();
  const { money } = useSettings();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (query: string) => {
    setLoading(true);
    try {
      setRows(await api<Record<string, unknown>[]>(`/catalog/products?q=${encodeURIComponent(query)}`));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => void load(q), 200);
    return () => clearTimeout(timer);
  }, [q, load]);

  const nm = (r: Record<string, unknown>) =>
    lang === 'ar' ? String(r.name_ar || r.name_en) : String(r.name_en || r.name_ar);

  return (
    <div className="col">
      <div className="row wrap">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('common.searchPlaceholder')} aria-label={t('common.searchPlaceholder')}
          style={{ maxWidth: 420 }}
        />
        <div className="spacer" />
        {can('catalog.edit') ? (
          <>
            <Button onClick={() => navigate('/products/import')}>{t('import.fromExcel')}</Button>
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('products.new')}
            </Button>
          </>
        ) : null}
      </div>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty
          icon="👕"
          title={t('products.empty')}
          hint={t('products.emptyHint')}
          action={
            can('catalog.edit') ? (
              <div className="row wrap" style={{ justifyContent: 'center' }}>
                <Button onClick={() => navigate('/products/import')}>{t('import.fromExcel')}</Button>
                <Button variant="primary" onClick={() => setCreating(true)}>{t('products.new')}</Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.product')}</th>
                  <th>{t('common.category')}</th>
                  <th className="num">{t('common.price')}</th>
                  <th className="num">{t('products.variants')}</th>
                  <th className="num">{t('common.stock')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id)} style={{ cursor: 'pointer' }} onClick={() => navigate(`/products/${r.id}`)}>
                    <td className="full strong" data-label={t('common.product')}>
                      <div className="list-product">
                        <Photo url={r.image_url as string | null} small className="list-thumb" />
                        <div>
                          {nm(r)}
                          {Number(r.active) === 0 ? <> <Pill kind="warn">{t('products.archived')}</Pill></> : null}
                          <div className="xsmall muted">{String(r.sku)}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label={t('common.category')}>
                      {lang === 'ar' ? String(r.category_ar ?? '') : String(r.category_en ?? '')}
                    </td>
                    <td className="num tnum" data-label={t('common.price')}>{money(Number(r.sale_price))}</td>
                    <td className="num" data-label={t('products.variants')}>{Number(r.variant_count)}</td>
                    <td className="num" data-label={t('common.stock')}>
                      <Pill kind={Number(r.stock) > 0 ? 'ok' : 'danger'}>{Number(r.stock)}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating ? (
        <ProductCreator
          onClose={() => setCreating(false)}
          onCreated={(made) => {
            setCreating(false);
            navigate(`/products/${made.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Creating a product: pick a size scale and colours, get the whole grid
// ---------------------------------------------------------------------------

/** What the dialog hands back once the product exists. */
export interface MadeProduct {
  id: string;
  variantIds: string[];
  /** The new items, ready to put on a list. Empty if the cache could not be read. */
  items: CatalogItem[];
  /** How many of each the person said there were. */
  qty: number;
  unitCost: number;
}

/**
 * Adding a product.
 *
 * Two screens open this. From Products it is "add a product to the catalogue",
 * and the quantity - if any - is recorded as an opening count. From Receive it
 * is "this style is new, put it on the delivery", and the quantity belongs to
 * the delivery, so this must NOT touch stock: the receive does that, and doing
 * both would count the same shipment twice.
 */
export function ProductCreator({
  onClose,
  onCreated,
  purpose = 'catalogue',
}: {
  onClose: () => void;
  onCreated: (made: MadeProduct) => void;
  purpose?: 'catalogue' | 'receiving';
}) {
  const t = useT();
  const { lang } = useI18n();
  const refs = useRefs();
  const toast = useToast();
  const { can } = useAuth();
  const { refreshCatalog } = useSync();

  const [name, setName] = useState('');
  const [salePrice, setSalePrice] = useState(0);
  const [scaleId, setScaleId] = useState('');
  const [sizes, setSizes] = useState<string[]>([]);
  const [colorIds, setColorIds] = useState<string[]>([]);
  const receiving = purpose === 'receiving';
  const [qty, setQty] = useState(receiving ? 1 : 0);

  // The rest, folded away until somebody asks for it.
  const [more, setMore] = useState(false);
  const [otherName, setOtherName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [costPrice, setCostPrice] = useState(0);
  const [finalSale, setFinalSale] = useState(false);
  const [openPrice, setOpenPrice] = useState(false);
  const [busy, setBusy] = useState(false);

  /*
   * A name and a price is a product. So one size is assumed until the shop says
   * otherwise, which is what lets the quick way out of this dialog be two fields.
   */
  useEffect(() => {
    if (scaleId || refs.sizeScales.length === 0) return;
    const one = refs.sizeScales.find((x) => x.kind === 'one') ?? refs.sizeScales[0];
    setScaleId(one.id);
  }, [refs.sizeScales, scaleId]);

  const scale = refs.sizeScales.find((x) => x.id === scaleId);
  useEffect(() => {
    if (scale?.sizes) setSizes(scale.sizes);
  }, [scale]);

  const count = sizes.length * Math.max(1, colorIds.length);
  const nm = (r: Ref) => (lang === 'ar' ? r.name_ar || r.name_en : r.name_en || r.name_ar);
  const canStock = can('stock.adjust');

  const create = async () => {
    setBusy(true);
    try {
      const res = await api<{ id: string; variantIds: string[] }>('/catalog/products', {
        body: {
          nameEn: lang === 'ar' ? otherName : name,
          nameAr: lang === 'ar' ? name : otherName,
          categoryId: categoryId || null, brandId: brandId || null,
          supplierId: supplierId || null, sizeScaleId: scaleId || null,
          sizes, colorIds, costPrice, salePrice, finalSale, openPrice,
        },
      });

      /*
       * What is on the rail right now, recorded as a count - not invented.
       * When the delivery is doing the receiving, it books the stock itself.
       */
      let stocked = false;
      if (!receiving && qty > 0 && canStock && res.variantIds.length > 0) {
        try {
          await api('/stock/adjust', {
            body: {
              reason: 'adjust_count',
              note: 'Opening stock',
              lines: res.variantIds.map((variantId) => ({ variantId, qty })),
            },
          });
          stocked = true;
        } catch {
          toast.err(t('products.stockLater'));
        }
      }

      toast.ok(stocked ? t('products.createdWithStock', { n: qty * res.variantIds.length }) : t('products.created'));
      await refreshCatalog();

      // The new items, as the rest of the app knows them.
      const made = await db.items.bulkGet(res.variantIds);
      onCreated({
        id: res.id,
        variantIds: res.variantIds,
        items: made.filter((x): x is CatalogItem => !!x),
        qty,
        unitCost: costPrice,
      });
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={receiving ? t('stock.newItemHere') : t('products.new')}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          {count > 1 ? <span className="small muted">{t('products.gridPreview', { n: count })}</span> : null}
          <Button
            variant="primary"
            size="lg"
            busy={busy}
            // An open-price item has no price and no size run to wait for.
            disabled={!name.trim() || (!openPrice && (salePrice <= 0 || sizes.length === 0))}
            onClick={() => void create()}
          >
            {receiving ? t('stock.addToDelivery') : t('products.saveProduct')}
          </Button>
        </>
      }
    >
      <div className="add-form">
      {/* The two things every product needs. */}
      <div className="grid c2">
        <Field label={t('products.itemName')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
            placeholder={t('products.itemNameHint')}
            autoFocus
          />
        </Field>
        {/* No price of its own, so no price field: the cashier types it. */}
        {openPrice ? null : (
          <Field label={t('products.salePrice')}>
            <MoneyInput value={salePrice} onValue={setSalePrice} />
          </Field>
        )}
      </div>

      {/*
       * A miscellaneous item, priced at the counter.
       *
       * It sits with the name because it changes what the rest of this form
       * means: there is no price to set, no size run, no colours and nothing to
       * count. Buried under "more details" it would only ever be found by
       * somebody who already knew it existed.
       */}
      <label className="checkline">
        <input type="checkbox" checked={openPrice} onChange={(e) => setOpenPrice(e.target.checked)} />
        {t('catalog.openPrice')}
      </label>
      {openPrice ? <p className="xsmall muted">{t('catalog.openPriceHint')}</p> : null}

      {/* Sizes: a shape to pick, then the squares you actually carry. */}
      {openPrice ? null : (
      <div className="field">
        <label>{t('products.whatSizes')}</label>
        <div className="btn-row">
          {refs.sizeScales.map((x) => (
            <Button key={x.id} variant={scaleId === x.id ? 'primary' : 'default'} onClick={() => setScaleId(x.id)}>
              {nm(x)}
            </Button>
          ))}
        </div>
        {(scale?.sizes?.length ?? 0) > 1 ? (
          <div className="btn-row" style={{ marginBlockStart: 'var(--s-2)' }}>
            {(scale?.sizes ?? []).map((x) => (
              <Button
                key={x}
                size="sm"
                variant={sizes.includes(x) ? 'primary' : 'default'}
                onClick={() => setSizes((cur) => (cur.includes(x) ? cur.filter((y) => y !== x) : [...cur, x]))}
              >
                {x}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
      )}

      {/* Colours: optional, and nothing is lost by skipping them. */}
      {openPrice ? null : (
      <div className="field">
        <label>{t('products.whatColours')}</label>
        <div className="btn-row">
          {refs.colors.map((c) => (
            <Button
              key={c.id}
              size="sm"
              variant={colorIds.includes(c.id) ? 'primary' : 'default'}
              onClick={() => setColorIds((cur) => (cur.includes(c.id) ? cur.filter((x) => x !== c.id) : [...cur, c.id]))}
            >
              <Swatch hex={c.hex} /> {nm(c)}
            </Button>
          ))}
        </div>
      </div>
      )}

      {/* Unpacking a box, the cost is right there on the invoice. */}
      {receiving ? (
        <Field label={t('products.costPrice')} hint={t('stock.costHint')}>
          <MoneyInput value={costPrice} onValue={setCostPrice} />
        </Field>
      ) : null}

      {/* How many you have, so it can be sold the minute you press save.
          Not for an open-price item: there is nothing on a shelf to count. */}
      {(canStock || receiving) && !openPrice ? (
        <Field
          label={receiving ? t('stock.howManyCame') : t('products.howMany')}
          hint={
            receiving
              ? count > 1
                ? t('stock.howManyCameEach')
                : undefined
              : count > 1
                ? t('products.howManyEach')
                : t('products.howManyHint')
          }
        >
          <div className="qty" style={{ width: 'fit-content' }}>
            <button type="button" onClick={() => setQty((n) => Math.max(0, n - 1))} aria-label="-">
              &minus;
            </button>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={qty}
              onChange={(e) => setQty(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              className="qty-num"
            />
            <button type="button" onClick={() => setQty((n) => n + 1)} aria-label="+">
              +
            </button>
          </div>
        </Field>
      ) : null}

      {/* ------------------------------------------------------------------ */}

      <button type="button" className="disclose" onClick={() => setMore((v) => !v)}>
        <span className="chev" aria-hidden>
          {more ? '\u25be' : '\u25b8'}
        </span>
        {t('products.moreDetails')}
      </button>

      {more ? (
        <div className="grid c2">
          <Field label={lang === 'ar' ? t('common.nameEn') : t('common.nameAr')}>
            <input
              value={otherName}
              onChange={(e) => setOtherName(e.target.value)}
              dir={lang === 'ar' ? 'ltr' : 'rtl'}
            />
          </Field>
          {receiving ? null : (
            <Field label={t('products.costPrice')}>
              <MoneyInput value={costPrice} onValue={setCostPrice} />
            </Field>
          )}
          <Field label={t('common.category')}>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">{t('common.none')}</option>
              {refs.categories.map((c) => (
                <option key={c.id} value={c.id}>{nm(c)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('common.brand')}>
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">{t('common.none')}</option>
              {refs.brands.map((b) => (
                <option key={b.id} value={b.id}>{nm(b)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('common.supplier')}>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">{t('common.none')}</option>
              {refs.suppliers.map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </select>
          </Field>
          <label className="checkline" style={{ alignSelf: 'end' }}>
            <input type="checkbox" checked={finalSale} onChange={(e) => setFinalSale(e.target.checked)} />
            {t('products.finalSale')}
          </label>
        </div>
      ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// One product: details, the grid, labels
// ---------------------------------------------------------------------------

/** The product's photo, and on a phone the camera is one tap away. */
function ProductPhoto({
  productId,
  url,
  canEdit,
  onChanged,
}: {
  productId: string;
  url: string | null;
  canEdit: boolean;
  onChanged: (url: string | null) => void;
}) {
  const t = useT();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);

  const failed = (e: unknown) => {
    if (e instanceof LogoError) return t(`photo.err.${e.reason}` as 'photo.err.not_image');
    const x = e as ApiError;
    if (x.status === 0) return t('net.needOnline');
    if (x.code === 'photo_too_big') return t('photo.err.too_big');
    if (x.code === 'photo_not_image') return t('photo.err.not_image');
    return t('photo.err.save');
  };

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const made = await photoFromFile(f);
      const r = await api<{ imageUrl: string }>(`/catalog/products/${productId}/photo`, {
        method: 'PUT',
        body: made,
        timeoutMs: 60_000,
      });
      onChanged(r.imageUrl);
      toast.ok(t('photo.saved'));
    } catch (err) {
      toast.err(failed(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setRemoving(false);
    setBusy(true);
    try {
      await api(`/catalog/products/${productId}/photo`, { method: 'DELETE' });
      onChanged(null);
    } catch (err) {
      toast.err(failed(err));
    } finally {
      setBusy(false);
    }
  };

  if (!url && !canEdit) return null;
  return (
    <div className="product-photo">
      {url ? (
        <Photo url={url} className="product-photo-img" />
      ) : (
        <button type="button" className="product-photo-empty" onClick={() => input.current?.click()} disabled={busy}>
          <span aria-hidden>📷</span>
          <span>{t('photo.add')}</span>
        </button>
      )}
      {canEdit ? (
        <>
          <input ref={input} type="file" accept="image/*" hidden onChange={(e) => void pick(e)} />
          {url ? (
            <div className="product-photo-actions">
              <Button size="sm" busy={busy} onClick={() => input.current?.click()}>{t('photo.change')}</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRemoving(true)}>{t('photo.remove')}</Button>
            </div>
          ) : busy ? (
            <div className="xsmall muted">{t('photo.saving')}</div>
          ) : null}
        </>
      ) : null}
      {removing ? (
        <Confirm
          title={t('photo.removeTitle')}
          message={t('photo.removeMsg')}
          confirmLabel={t('photo.remove')}
          danger
          onConfirm={() => void remove()}
          onCancel={() => setRemoving(false)}
        />
      ) : null}
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const { lang } = useI18n();
  const { can } = useAuth();
  const { money } = useSettings();
  const toast = useToast();
  const { refreshCatalog } = useSync();
  const navigate = useNavigate();
  const refs = useRefs();

  const [product, setProduct] = useState<Record<string, unknown> | null>(null);
  const [variants, setVariants] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [barcodeFor, setBarcodeFor] = useState<CatalogItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ product: Record<string, unknown>; variants: CatalogItem[] }>(`/catalog/products/${id}`);
      setProduct(res.product);
      setVariants(res.variants);
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const grid = useMemo(() => {
    const sizes = [...new Set(variants.map((v) => v.size))].sort(compareSizes);
    const colors = [...new Map(variants.map((v) => [v.colorEn + v.colorAr, v])).values()];
    return { sizes, colors };
  }, [variants]);

  if (loading) return <Loading />;
  if (!product) return <Empty icon="🤷" title={t('error.notFound')} />;

  const nm = lang === 'ar' ? String(product.name_ar) : String(product.name_en);
  const totalStock = variants.reduce((a, v) => a + v.stock, 0);

  return (
    <div className="col">
      <div className="card product-top">
        <ProductPhoto
          productId={String(product.id)}
          url={(product.image_url as string | null) ?? null}
          canEdit={can('catalog.edit')}
          onChanged={(url) => {
            setProduct({ ...product, image_url: url });
            void refreshCatalog().catch(() => undefined);
          }}
        />
        <div className="product-top-main">
          <div className="card-head">
            <div>
              <h2>{nm}</h2>
              <div className="small muted">
                {lang === 'ar' ? String(product.name_en) : String(product.name_ar)} · {String(product.sku)}
              </div>
            </div>
            <div className="spacer" />
            <Button variant="ghost" onClick={() => navigate('/products')}>{t('common.back')}</Button>
            {can('catalog.edit') ? (
              <>
                <Button onClick={() => setEditing(true)}>{t('common.edit')}</Button>
                <Button onClick={() => setAdding(true)}>{t('products.addVariants')}</Button>
              </>
            ) : null}
            {can('labels.print') ? (
              <Button variant="primary" onClick={() => setPrinting(true)}>{t('labels.title')}</Button>
            ) : null}
          </div>

          <div className="row wrap">
            <Pill kind="accent">{money(Number(product.sale_price))}</Pill>
            <Pill>{t('products.costPrice')}: {money(Number(product.cost_price))}</Pill>
            <Pill kind={totalStock > 0 ? 'ok' : 'danger'}>{t('products.totalStock')}: {totalStock}</Pill>
            {Number(product.final_sale) === 1 ? <Pill kind="warn">{t('products.finalSale')}</Pill> : null}
            {Number(product.active) === 0 ? <Pill kind="danger">{t('products.archived')}</Pill> : null}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{t('products.grid')}</h3>
          <div className="spacer" />
          <span className="xsmall muted">{t('stock.inStock')}</span>
        </div>

        <div className="sizegrid table-wrap">
          <table>
            <thead>
              <tr>
                <th className="corner">{t('common.colour')}</th>
                {grid.sizes.map((s) => (
                  <th key={s} className="num">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.colors.map((c) => (
                <tr key={c.colorEn + c.colorAr}>
                  <td className="rowhead">
                    <Swatch hex={c.colorHex} /> {lang === 'ar' ? c.colorAr || c.colorEn : c.colorEn || c.colorAr}
                  </td>
                  {grid.sizes.map((s) => {
                    const v = variants.find(
                      (x) => x.size === s && x.colorEn === c.colorEn && x.colorAr === c.colorAr,
                    );
                    if (!v) return <td key={s}><div className="cell-stock none">—</div></td>;
                    const cls = v.stock <= 0 ? 'zero' : v.stock <= v.lowStockThreshold ? 'low' : 'ok';
                    return (
                      <td key={s}>
                        <button
                          className={`cell-stock ${cls}`}
                          style={{ border: 'none', cursor: 'pointer', font: 'inherit' }}
                          onClick={() => setBarcodeFor(v)}
                          title={v.barcodes[0]}
                        >
                          {v.stock}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {barcodeFor ? (
        <VariantDialog
          item={barcodeFor}
          onClose={() => setBarcodeFor(null)}
          onSaved={() => {
            setBarcodeFor(null);
            void load();
            void refreshCatalog();
          }}
        />
      ) : null}

      {editing ? (
        <ProductEditor
          product={product}
          refs={refs}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
            void refreshCatalog();
          }}
          onArchive={() => {
            setEditing(false);
            setArchiving(true);
          }}
        />
      ) : null}

      {archiving ? (
        <Confirm
          title={t('products.archive')}
          message={t('products.archiveNote')}
          confirmLabel={t('products.archive')}
          danger
          onConfirm={() => {
            void api(`/catalog/products/${id}`, { method: 'DELETE' }).then(() => {
              toast.ok(t('common.saved'));
              void load();
              void refreshCatalog();
            });
            setArchiving(false);
          }}
          onCancel={() => setArchiving(false)}
        />
      ) : null}

      {adding && id ? (
        <VariantAdder
          productId={id}
          existing={variants}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await load();
          }}
        />
      ) : null}

      {printing ? <LabelPrinter items={variants} onClose={() => setPrinting(false)} /> : null}
    </div>
  );
}

function VariantDialog({
  item,
  onClose,
  onSaved,
}: {
  item: CatalogItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const toast = useToast();
  const [override, setOverride] = useState(item.price);
  const [threshold, setThreshold] = useState(item.lowStockThreshold);
  const [extra, setExtra] = useState('');

  const save = async () => {
    try {
      await api(`/catalog/variants/${item.variantId}`, {
        method: 'PUT',
        body: { priceOverride: override, lowStockThreshold: threshold },
      });
      toast.ok(t('common.saved'));
      onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  const addBarcode = async () => {
    try {
      await api(`/catalog/variants/${item.variantId}/barcodes`, { body: { code: extra } });
      toast.ok(t('common.saved'));
      setExtra('');
      onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  return (
    <Modal
      title={`${lang === 'ar' ? item.nameAr || item.nameEn : item.nameEn || item.nameAr} · ${item.size}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" onClick={() => void save()}>{t('common.save')}</Button>
        </>
      }
    >
      <div className="col">
        <div className="center" style={{ background: '#fff', padding: 'var(--s-4)', borderRadius: 'var(--radius)' }}>
          {item.barcodes.map((b) => (
            <div key={b} style={{ marginBlockEnd: 'var(--s-3)' }}>
              <Barcode value={b} />
            </div>
          ))}
        </div>

        <div className="row">
          <Pill kind={item.stock > 0 ? 'ok' : 'danger'}>{t('common.stock')}: {item.stock}</Pill>
          <Pill>{money(item.price)}</Pill>
        </div>

        <Field label={t('products.priceOverride')}>
          <MoneyInput value={override} onValue={setOverride} />
        </Field>
        <Field label={t('products.lowStockAt')}>
          <input type="number" min={0} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} />
        </Field>

        <Field label={t('products.extraBarcode')} hint={t('products.extraBarcodeHint')}>
          <div className="row">
            <input value={extra} onChange={(e) => setExtra(e.target.value)} data-scan-target="true" />
            <Button onClick={() => void addBarcode()} disabled={!extra.trim()}>{t('common.add')}</Button>
          </div>
        </Field>
      </div>
    </Modal>
  );
}

function ProductEditor({
  product,
  refs,
  onClose,
  onSaved,
  onArchive,
}: {
  product: Record<string, unknown>;
  refs: ReturnType<typeof useRefs>;
  onClose: () => void;
  onSaved: () => void;
  onArchive: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const [form, setForm] = useState({
    nameEn: String(product.name_en ?? ''),
    nameAr: String(product.name_ar ?? ''),
    categoryId: String(product.category_id ?? ''),
    brandId: String(product.brand_id ?? ''),
    supplierId: String(product.supplier_id ?? ''),
    season: String(product.season ?? ''),
    costPrice: Number(product.cost_price),
    salePrice: Number(product.sale_price),
    finalSale: Number(product.final_sale) === 1,
    openPrice: Number(product.open_price) === 1,
  });

  const nm = (r: Ref) => (lang === 'ar' ? r.name_ar || r.name_en : r.name_en || r.name_ar);

  const save = async () => {
    try {
      await api(`/catalog/products/${product.id}`, { method: 'PUT', body: form });
      toast.ok(t('common.saved'));
      onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  return (
    <Modal
      title={t('products.edit')}
      wide
      onClose={onClose}
      footer={
        <>
          <Button variant="danger" onClick={onArchive}>{t('products.archive')}</Button>
          <div className="spacer" />
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={() => void save()}>{t('common.save')}</Button>
        </>
      }
    >
      <div className="grid c2">
        <Field label={t('common.nameAr')}>
          <input value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} dir="rtl" />
        </Field>
        <Field label={t('common.nameEn')}>
          <input value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} dir="ltr" />
        </Field>
        <Field label={t('common.category')}>
          <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
            <option value="">{t('common.none')}</option>
            {refs.categories.map((c) => <option key={c.id} value={c.id}>{nm(c)}</option>)}
          </select>
        </Field>
        <Field label={t('common.brand')}>
          <select value={form.brandId} onChange={(e) => setForm({ ...form, brandId: e.target.value })}>
            <option value="">{t('common.none')}</option>
            {refs.brands.map((b) => <option key={b.id} value={b.id}>{nm(b)}</option>)}
          </select>
        </Field>
        <Field label={t('common.supplier')}>
          <select value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })}>
            <option value="">{t('common.none')}</option>
            {refs.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label={t('products.season')}>
          <input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} />
        </Field>
        <Field label={t('products.costPrice')}>
          <MoneyInput value={form.costPrice} onValue={(v) => setForm({ ...form, costPrice: v })} />
        </Field>
        <Field label={t('products.salePrice')}>
          <MoneyInput value={form.salePrice} onValue={(v) => setForm({ ...form, salePrice: v })} />
        </Field>
      </div>
      <label className="checkline">
        <input
          type="checkbox"
          checked={form.finalSale}
          onChange={(e) => setForm({ ...form, finalSale: e.target.checked })}
        />
        {t('products.finalSale')}
      </label>
      {/* For a miscellaneous item: the cashier types the price, and nothing is
          counted for it on the shelf. */}
      <label className="checkline">
        <input
          type="checkbox"
          checked={form.openPrice}
          onChange={(e) => setForm({ ...form, openPrice: e.target.checked })}
        />
        {t('catalog.openPrice')}
      </label>
      <p className="xsmall muted">{t('catalog.openPriceHint')}</p>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Growing a product that already exists
// ---------------------------------------------------------------------------

/**
 * A style rarely arrives finished. The coat sold in three colours all winter
 * turns up in green, or the supplier finally makes it in XXXXL, and until now
 * the only way to record that was to invent a second product.
 *
 * Tick the sizes and colours wanted and every missing square of the grid is
 * filled in - each one with its own barcode, ready to be received. Squares that
 * already exist are left exactly as they are, stock and all, so pressing this
 * twice is harmless.
 */
function VariantAdder({
  productId,
  existing,
  onClose,
  onAdded,
}: {
  productId: string;
  existing: CatalogItem[];
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const refs = useRefs();
  const { refreshCatalog } = useSync();

  const nm = (x: { name_en: string; name_ar: string }) =>
    lang === 'ar' ? x.name_ar || x.name_en : x.name_en || x.name_ar;

  // What the product is made of today, so the form can offer what is missing.
  const haveSizes = useMemo(() => new Set(existing.map((v) => v.size)), [existing]);
  const haveColourNames = useMemo(() => new Set(existing.map((v) => v.colorEn)), [existing]);
  const squares = useMemo(
    () => new Set(existing.map((v) => `${v.size}|${v.colorEn}`)),
    [existing],
  );

  const [sizes, setSizes] = useState<string[]>([...haveSizes].sort(compareSizes));
  const [colourIds, setColourIds] = useState<string[]>(
    refs.colors.filter((c) => haveColourNames.has(c.name_en)).map((c) => c.id),
  );
  const [busy, setBusy] = useState(false);

  // Colours the product already has must stay ticked - a colour cannot be taken
  // away here, only added, because the garments already exist.
  const lockedColourIds = useMemo(
    () => new Set(refs.colors.filter((c) => haveColourNames.has(c.name_en)).map((c) => c.id)),
    [refs.colors, haveColourNames],
  );

  // Every size the shop knows about, so a size outside this product's own scale
  // can still be added when the supplier sends one.
  const allSizes = useMemo(() => {
    const set = new Set<string>(haveSizes);
    for (const scale of refs.sizeScales) for (const x of scale.sizes ?? []) set.add(x);
    return [...set].sort(compareSizes);
  }, [refs.sizeScales, haveSizes]);

  const chosenColours = refs.colors.filter((c) => colourIds.includes(c.id));
  const toMake = useMemo(() => {
    const out: { size: string; colorId: string | null; label: string }[] = [];
    for (const size of sizes) {
      if (chosenColours.length === 0) {
        if (!squares.has(`${size}|`)) out.push({ size, colorId: null, label: size });
        continue;
      }
      for (const c of chosenColours) {
        if (!squares.has(`${size}|${c.name_en}`)) {
          out.push({ size, colorId: c.id, label: `${size} ${nm(c)}` });
        }
      }
    }
    return out;
  }, [sizes, chosenColours, squares]);

  const add = async () => {
    setBusy(true);
    let made = 0;
    try {
      // One at a time: the server gives each new square its own barcode, and
      // sending them separately means a failure halfway still keeps the rest.
      for (const one of toMake) {
        const res = await api<{ created: boolean }>(`/catalog/products/${productId}/variants`, {
          body: { size: one.size, colorId: one.colorId },
        });
        if (res.created) made += 1;
      }
      await refreshCatalog();
      toast.ok(t('products.variantsAdded', { n: made }));
      await onAdded();
    } catch (e) {
      toast.err((e as Error).message);
      if (made > 0) await onAdded();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('products.addVariants')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" busy={busy} disabled={toMake.length === 0} onClick={() => void add()}>
            {toMake.length > 0 ? t('products.addNSquares', { n: toMake.length }) : t('products.nothingNew')}
          </Button>
        </>
      }
    >
      <div className="col">
        <p className="small muted">{t('products.addVariantsHint')}</p>

        <div className="field">
          <label>{t('products.sizesToHave')}</label>
          <div className="btn-row">
            {allSizes.map((x) => (
              <Button
                key={x}
                size="sm"
                variant={sizes.includes(x) ? 'primary' : 'default'}
                disabled={haveSizes.has(x)}
                onClick={() => setSizes((cur) => (cur.includes(x) ? cur.filter((y) => y !== x) : [...cur, x]))}
              >
                {x}
              </Button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>{t('products.coloursToHave')}</label>
          <div className="btn-row">
            {refs.colors.map((c) => (
              <Button
                key={c.id}
                size="sm"
                variant={colourIds.includes(c.id) ? 'primary' : 'default'}
                disabled={lockedColourIds.has(c.id)}
                onClick={() =>
                  setColourIds((cur) => (cur.includes(c.id) ? cur.filter((x) => x !== c.id) : [...cur, c.id]))
                }
              >
                <Swatch hex={c.hex} /> {nm(c)}
              </Button>
            ))}
          </div>
        </div>

        {toMake.length > 0 ? (
          <div className="field">
            <label>{t('products.willBeAdded')}</label>
            <div className="btn-row">
              {toMake.map((x) => (
                <span key={`${x.size}-${x.colorId ?? ''}`} className="pill accent">{x.label}</span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
